import { useEffect, useState } from 'react'
import { Check } from 'lucide-react'

import { useLanguage } from '../shared/LanguageContext'
import { createMutationId, displayDate, sessionsForClass, studentsForClass } from './taFormat'
import { taCopy } from './taCopy'
import { SectionUnavailable, TaSubnav } from './TaShell'
import { useTaDraft } from './useTaDraft'
import { releaseConflictedMutationKey } from './taMutationKey'

const EMPTY_OBSERVATION = {
  class_id: '', session_id: '', student_id: '', observation_text: '', client_mutation_id: '',
}
const EMPTY_SUPPORT = {
  class_id: '', session_id: '', student_ids: [], activity_type: 'wechat_questions',
  occurred_at: '', duration_minutes: '15', narrative: '', client_mutation_id: '',
}

function ClassSessionFields({ draft, setDraft, schedule, copy, language, sessionOptional = false }) {
  const sessions = sessionsForClass(schedule, draft.class_id)
  const classes = schedule?.classes || []
  const firstClassId = classes[0]?.id || ''
  const draftClassInScope = classes.some((row) => row.id === draft.class_id)
  useEffect(() => {
    // Default to the first class, and rescue a persisted draft whose class
    // left scope (e.g. its semester ended): a controlled select with no
    // matching option renders blank and the submit can only 403.
    if ((!draft.class_id || !draftClassInScope) && firstClassId) {
      setDraft((prev) => (
        classes.some((row) => row.id === prev.class_id)
          ? prev
          : { ...prev, class_id: firstClassId, session_id: '', student_id: '', student_ids: [] }
      ))
    }
  }, [draft.class_id, draftClassInScope, firstClassId, setDraft])  // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="ta-v2-form-grid">
      <label>{copy('class')}
        <select value={draft.class_id} onChange={(event) => setDraft({
          ...draft, class_id: event.target.value, session_id: '', student_id: '', student_ids: [],
        })} required>
          <option value="">{copy('chooseClass')}</option>
          {(schedule?.classes || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </select>
      </label>
      <label>{sessionOptional ? copy('optionalSession') : copy('session')}
        <select value={draft.session_id} onChange={(event) => setDraft({ ...draft, session_id: event.target.value })} required={!sessionOptional}>
          <option value="">{copy('chooseSession')}</option>
          {sessions.map((row) => (
            <option key={row.id} value={row.id}>{displayDate(row.session_date, language)} · {copy('session')} {row.session_number}</option>
          ))}
        </select>
      </label>
    </div>
  )
}

function FeedbackLine({ state }) {
  if (!state) return null
  return <p className={`ta-v2-form-status is-${state.type}`} role="status">{state.message}</p>
}

function TaskList({ tasks, copy, language, onAction }) {
  const [notes, setNotes] = useState({})
  const [saving, setSaving] = useState(null)
  const [status, setStatus] = useState(null)
  const open = tasks.filter((row) => ['open', 'reopened'].includes(row.status))
  async function complete(task) {
    setSaving(task.id)
    setStatus(null)
    const result = await onAction({ action: 'complete_task', task_id: task.id, completion_note: notes[task.id] || '' })
    setSaving(null)
    setStatus(result.error
      ? { type: 'error', message: result.error }
      : { type: 'success', message: copy('saved') })
  }
  return (
    <section className="ta-v2-section ta-v2-section-first">
      <div className="ta-v2-section-heading"><h2>{copy('openTasks')}</h2><span>{open.length}</span></div>
      {open.length ? <div className="ta-v2-task-list">{open.map((task) => (
        <article key={task.id} className="ta-v2-task">
          <div className="ta-v2-task-main">
            <div className="ta-v2-task-copy">
              <h3>{task.title}</h3>
              {task.details && <p>{task.details}</p>}
              {task.due_at && <small>{copy('due')} · {displayDate(task.due_at, language)}</small>}
            </div>
            <button type="button" className="ta-v2-secondary-button" disabled={saving === task.id} onClick={() => complete(task)}>
              <Check aria-hidden="true" size={18} />{saving === task.id ? copy('saving') : copy('markComplete')}
            </button>
          </div>
          <label className="ta-v2-task-note">
            <span>{copy('completionNote')}</span>
            <input value={notes[task.id] || ''} onChange={(event) => setNotes({ ...notes, [task.id]: event.target.value })} />
          </label>
        </article>
      ))}</div> : <p className="ta-v2-empty">{copy('noTasks')}</p>}
      <FeedbackLine state={status} />
    </section>
  )
}

function ObservationForm({ schedule, copy, language, onAction }) {
  const [draft, setDraft, clearDraft] = useTaDraft('observation', EMPTY_OBSERVATION)
  const [status, setStatus] = useState(null)
  const [saving, setSaving] = useState(false)
  const students = studentsForClass(schedule, draft.class_id)
  async function submit(event) {
    event.preventDefault()
    setSaving(true)
    setStatus(null)
    const payload = { ...draft, action: 'save_observation', client_mutation_id: draft.client_mutation_id || createMutationId() }
    setDraft(payload)
    const result = await onAction(payload)
    setSaving(false)
    if (releaseConflictedMutationKey(result, setDraft)) {
      return setStatus({ type: 'error', message: copy('mutationKeyConflict') })
    }
    if (result.error) return setStatus({ type: 'error', message: result.error })
    clearDraft(EMPTY_OBSERVATION)
    setStatus({ type: 'success', message: copy('saved') })
  }
  return (
    <section className="ta-v2-work-form" aria-labelledby="ta-observation-form-heading">
      <h2 id="ta-observation-form-heading">{copy('addObservation')}</h2>
      <form onSubmit={submit}>
        <p className="ta-v2-helper">{copy('observationHelp')}</p>
        <ClassSessionFields draft={draft} setDraft={setDraft} schedule={schedule} copy={copy} language={language} />
        <label>{copy('student')}
          <select value={draft.student_id} onChange={(event) => setDraft({ ...draft, student_id: event.target.value })} required>
            <option value="">{copy('chooseStudent')}</option>
            {students.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </label>
        <label>{copy('observation')}
          <textarea rows="4" value={draft.observation_text} onChange={(event) => setDraft({ ...draft, observation_text: event.target.value })} required />
        </label>
        <button className="ta-v2-primary-button" disabled={saving}>{saving ? copy('saving') : copy('submitObservation')}</button>
        <FeedbackLine state={status} />
      </form>
    </section>
  )
}

function SupportForm({ schedule, copy, language, onAction }) {
  const [draft, setDraft, clearDraft] = useTaDraft('support', EMPTY_SUPPORT)
  const [status, setStatus] = useState(null)
  const [saving, setSaving] = useState(false)
  const students = studentsForClass(schedule, draft.class_id)
  const activities = ['in_school_meeting', 'wechat_questions', 'document_review', 'conflict_help', 'class_support', 'other']
  async function submit(event) {
    event.preventDefault()
    if (!draft.student_ids.length) return setStatus({ type: 'error', message: copy('selectAtLeastOne') })
    setSaving(true)
    setStatus(null)
    const payload = {
      ...draft, action: 'save_support_log', duration_minutes: Number(draft.duration_minutes),
      client_mutation_id: draft.client_mutation_id || createMutationId(),
    }
    setDraft(payload)
    const result = await onAction(payload)
    setSaving(false)
    if (releaseConflictedMutationKey(result, setDraft)) {
      return setStatus({ type: 'error', message: copy('mutationKeyConflict') })
    }
    if (result.error) return setStatus({ type: 'error', message: result.error })
    clearDraft(EMPTY_SUPPORT)
    setStatus({ type: 'success', message: copy('saved') })
  }
  return (
    <section className="ta-v2-work-form" aria-labelledby="ta-support-form-heading">
      <h2 id="ta-support-form-heading">{copy('logSupport')}</h2>
      <form onSubmit={submit}>
        <p className="ta-v2-helper">{copy('supportHelp')}</p>
        <ClassSessionFields draft={draft} setDraft={setDraft} schedule={schedule} copy={copy} language={language} sessionOptional />
        <label>{copy('activity')}
          <select value={draft.activity_type} onChange={(event) => setDraft({ ...draft, activity_type: event.target.value })}>
            {activities.map((value) => <option key={value} value={value}>{copy(`activity_${value}`)}</option>)}
          </select>
        </label>
        <div className="ta-v2-form-grid">
          <label>{copy('when')}<input type="date" value={draft.occurred_at} onChange={(event) => setDraft({ ...draft, occurred_at: event.target.value })} required /></label>
          <label>{copy('minutes')}<input type="number" min="0" max="1440" inputMode="numeric" value={draft.duration_minutes} onChange={(event) => setDraft({ ...draft, duration_minutes: event.target.value })} required /></label>
        </div>
        <label>{copy('student')}
          <select value={draft.student_ids[0] || ''} onChange={(event) => setDraft({ ...draft, student_ids: event.target.value ? [event.target.value] : [] })} required>
            <option value="">{copy('chooseStudent')}</option>
            {students.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </label>
        <label>{copy('narrative')}<textarea rows="4" value={draft.narrative} onChange={(event) => setDraft({ ...draft, narrative: event.target.value })} required /></label>
        <button className="ta-v2-primary-button" disabled={saving}>{saving ? copy('saving') : copy('submitSupport')}</button>
        <FeedbackLine state={status} />
      </form>
    </section>
  )
}

function ObservationHistory({ observations, copy, language }) {
  return (
    <section className="ta-v2-section">
      <div className="ta-v2-section-heading"><h2>{copy('recentObservations')}</h2><span>{observations.length}</span></div>
      {observations.length ? <div className="ta-v2-feedback-list">{observations.map((row) => (
        <article key={row.id}>
          <header><strong>{row.student_name || copy('student')}</strong><time>{displayDate(row.created_at, language)}</time></header>
          <p>{row.observation_text}</p><small>{copy(row.state)}</small>
        </article>
      ))}</div> : <p className="ta-v2-empty">{copy('noRecentObservations')}</p>}
    </section>
  )
}

function SupportHistory({ supportLogs, copy, language }) {
  return (
    <section className="ta-v2-section">
      <div className="ta-v2-section-heading"><h2>{copy('recentSupport')}</h2><span>{supportLogs.length}</span></div>
      {supportLogs.length ? <div className="ta-v2-feedback-list">{supportLogs.map((row) => (
        <article key={row.id}>
          <header><strong>{copy(`activity_${row.activity_type}`)}</strong><time>{displayDate(row.occurred_at, language)}</time></header>
          <p>{row.narrative}</p><small>{row.duration_minutes} {copy('minutes')}</small>
        </article>
      ))}</div> : <p className="ta-v2-empty">{copy('noRecentSupport')}</p>}
    </section>
  )
}

const WORK_TABS = [
  { id: 'tasks', label: 'openTasks' },
  { id: 'observations', label: 'observations' },
  { id: 'support', label: 'support' },
]

export default function TaWorkView({ home, onAction, focusTarget }) {
  const { language } = useLanguage()
  const copy = taCopy(language)
  const [activeTab, setActiveTab] = useState(() => (
    focusTarget === 'observation' ? 'observations' : focusTarget === 'support' ? 'support' : 'tasks'
  ))
  const schedule = home.schedule?.status === 'ready' ? home.schedule.data : null
  const tasks = home.tasks?.status === 'ready' ? home.tasks.data : null
  const evidence = home.evidence?.status === 'ready' ? home.evidence.data : null
  return (
    <div className="ta-v2-view">
      <TaSubnav
        tabs={WORK_TABS.map((tab) => ({ id: tab.id, label: copy(tab.label) }))}
        active={activeTab}
        onChange={setActiveTab}
        label={copy('work')}
        idPrefix="ta-work"
      />

      <div id={`ta-work-panel-${activeTab}`} role="tabpanel" aria-labelledby={`ta-work-tab-${activeTab}`}>
        {activeTab === 'tasks' && (tasks
          ? <TaskList tasks={tasks} copy={copy} language={language} onAction={onAction} />
          : <SectionUnavailable />)}
        {activeTab === 'observations' && <>
          {schedule ? <ObservationForm schedule={schedule} copy={copy} language={language} onAction={onAction} /> : <SectionUnavailable />}
          {evidence ? <ObservationHistory observations={evidence.observations} copy={copy} language={language} /> : <SectionUnavailable />}
        </>}
        {activeTab === 'support' && <>
          {schedule ? <SupportForm schedule={schedule} copy={copy} language={language} onAction={onAction} /> : <SectionUnavailable />}
          {evidence ? <SupportHistory supportLogs={evidence.support_logs} copy={copy} language={language} /> : <SectionUnavailable />}
        </>}
      </div>
    </div>
  )
}
